// CookieYes "advertisement" category gate.
//
// The Meta browser pixel only fires once CookieYes reports advertisement
// consent, so any SERVER-side twin (meta-capi-download) must sit behind the
// exact same gate — otherwise we'd report conversions for visitors who declined
// ad cookies, and the server count would drift above the browser count.
//
// Primary source is the CookieYes JS API; the cookie parse is the fallback for
// the window between page load and the script booting.

declare global {
  interface Window {
    getCkyConsent?: () => {
      categories?: Record<string, boolean>;
    } | undefined;
  }
}

const readCookie = (name: string): string | undefined => {
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
};

// The isolated desktop checkout has a different cookie jar from the website.
// Main supplies its current, persisted website choice with the verified receipt.
// This fallback lives only for this return page; it never creates a cookie or
// supersedes a decision already saved in this browser.
let nativeReceiptConsent: boolean | undefined;
export function adoptNativeReceiptConsent(value: 'granted' | 'denied'): void {
  nativeReceiptConsent = value === 'granted';
  const granted = value === 'granted' && marketingConsentGranted();
  window.gtag?.('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
  });
}

/** True only when the visitor accepted the advertisement cookie category. */
export const marketingConsentGranted = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;

    const persisted = readCookie('cookieyes-consent');
    if (!persisted && nativeReceiptConsent !== undefined) return nativeReceiptConsent;

    const api = window.getCkyConsent?.();
    if (api?.categories && typeof api.categories.advertisement === 'boolean') {
      return api.categories.advertisement === true;
    }

    // Fallback: the cookieyes-consent cookie is a flat
    // `key:value,key:value` string, e.g. "...,advertisement:yes,...".
    const raw = persisted;
    if (!raw) return false;
    return raw
      .split(',')
      .map((pair) => pair.split(':'))
      .some(([k, v]) => k?.trim() === 'advertisement' && v?.trim() === 'yes');
  } catch {
    return false;
  }
};

/**
 * CookieYes "analytics" category: true or false once the banner (or a saved
 * choice) reports it, undefined while no decision is visible yet. A decline opts PostHog out of
 * analytics and session replay on the website.
 */
export const analyticsConsent = (): boolean | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    const api = window.getCkyConsent?.();
    if (api?.categories && typeof api.categories.analytics === 'boolean') return api.categories.analytics;
    const raw = readCookie('cookieyes-consent');
    if (!raw) return undefined;
    const pair = raw.split(',').map((p) => p.split(':')).find(([k]) => k?.trim() === 'analytics');
    return pair ? pair[1]?.trim() === 'yes' : undefined;
  } catch {
    return undefined;
  }
};

/**
 * `marketing_consent=granted|denied` for the `vovy://` deep links (Welcome and
 * Open pages). The desktop app has no cookie banner; it persists this decision
 * and sends it with its own create-checkout-session request, the same way every
 * web checkout caller does through checkoutTrackingMetadata. Always explicit:
 * "no decision yet" is denied, which is the server's default for an absent
 * field.
 */
export const marketingConsentDeeplinkParam = (): string =>
  `marketing_consent=${marketingConsentGranted() ? 'granted' : 'denied'}`;
